import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import EnterVRIcon from "./EnterVRIcon";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Unity, { UnityContext } from "react-unity-webgl";
import * as Sentry from "@sentry/nextjs";
import { IconButton } from "@mui/material";
import LinearProgress, { LinearProgressProps } from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import XMLPatch from './XMLPatch';

const unityContext = new UnityContext({
  loaderUrl: "Build/netlify.loader.js",
  dataUrl: "Build/netlify.data.br",
  frameworkUrl: "Build/netlify.framework.js.br",
  codeUrl: "Build/netlify.wasm.br",
  // Available since react-unity-webgl@8.8.0
  symbolsUrl: "Build/netlify.symbols.json.br",
  streamingAssetsUrl: "StreamingAssets"
});

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

const UnityWebGL = () => {
  const [isFocus, setFocus] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorPopup, setErrorPopup] = useState(false);
  const [infoPopup, setInfoPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [infoTitle, setInfoTitle] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [progress, setProgress] = useState(0);

  const handleErrorOpen = (message: string) => {
    setErrorMessage(message);
    setErrorPopup(true);
  };

  const handleErrorClose = () => {
    setErrorPopup(false);
  };

  const sendBugReport = async () => {
    setErrorPopup(false);
  };

  const handleInfoOpen = (title: string, message: string) => {
    setInfoTitle(title);
    setInfoMessage(message);
    setInfoPopup(true);
  };

  const handleInfoClose = () => {
    setInfoPopup(false);
  };

  const inVR = async () => {
    // @ts-ignore
    let webXRSupport = await navigator.xr.isSessionSupported("immersive-vr");
    if (webXRSupport) {
      unityContext.unityInstance.Module.WebXR.toggleVR();
    } else handleInfoOpen("WebXR Not supported", "Sorry your device does not support WebXR");
  };

  useEffect(function() {
    let LoadingTransaction = Sentry.startTransaction({ name: "unity-content-loading" });
    let runtimeLoadingTransaction = LoadingTransaction.startChild({
      op: "runtime-loading",
      description: "operation for loading the unity runtime"
    });
    let firstSceneLoadingTransaction = LoadingTransaction.startChild({
      op: "first-scene-loading",
      description: "operation for loading the first scene"
    });
    unityContext.on("progress", function(progression) {
      setProgress(progression * 100);
    });
    unityContext.on("loaded", function() {
      setIsLoaded(true);
      runtimeLoadingTransaction.setStatus("OK");
      runtimeLoadingTransaction.finish();
      unityContext.on("error", handleErrorOpen);
      // Lock pointer when user re-gain focus at unity canvas
      // TODO : Change mouse control in unity project first
      // Requiring browser permission
      // unityContext.htmlCanvasElement?.addEventListener("focus", async function() {
      //   if (!isFocus) {
      //     setFocus(true);
      //     // unityContext.requestPointerLock();
      //     await unityContext.htmlCanvasElement?.requestPointerLock();
      //     // unityContext.setFullscreen(true);
      //     await unityContext.htmlCanvasElement?.requestFullscreen();
      //   }
      // }, false);
    });
    unityContext.on("WebXRReady", function() {
      // TODO : handle 1st scene loaded event here
      firstSceneLoadingTransaction.setStatus("OK");
      firstSceneLoadingTransaction.finish();
      LoadingTransaction.finish();
    });
    unityContext.on("CaptureMessage", function(data) {
      Sentry.captureMessage(data.message, { tags: data.tags });
    });
    unityContext.on("SendTransaction", function(data) {
      let t = Sentry.startTransaction(data);
      t.finish();
    });
    unityContext.on("AddBreadcrumb", function(breadcrumb) {
      Sentry.addBreadcrumb(breadcrumb);
    });
  });

  XMLPatch();

  return <div style={{ textAlign: "center" }}>
    <Unity unityContext={unityContext}
           style={{
             // marginTop: "1em",
             height: "100%",
             width: "100%",
             background: "grey"
           }}
           tabIndex={1}
           matchWebGLToCanvasSize={true}
    />
    {!isLoaded &&
      <LinearProgressWithLabel value={progress} />
    }
    {/*todo : re-write this to use https://mui.com/zh/components/buttons/#complex-button*/}
    <IconButton color="primary" onClick={inVR} sx={{
      position: "fixed",
      bottom: 0,
      right: 0
    }}>
      <EnterVRIcon viewBox="0 0 1024 1024" fontSize="large" />
    </IconButton>
    <Dialog open={errorPopup} onClose={handleErrorClose} aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">
        {"There is an error occurred, do you want to report this?"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {errorMessage}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleErrorClose}>Restart</Button>
        <Button onClick={sendBugReport} autoFocus>Send</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={infoPopup} onClose={handleInfoClose}>
      <DialogTitle id="info-dialog-title">
        {infoTitle}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="info-dialog-description">
          {infoMessage}
        </DialogContentText>
        <DialogActions>
          <Button onClick={handleInfoClose}>OK</Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  </div>;
};

export default UnityWebGL;